/**
 * @file HeightValueField.tsx
 * @description Число высоты секции для режимов «Числом» (px, 0–1600) и
 * «Экран» (% окна без шапки, 0–100; витрина: (100svh − шапка) × N/100;
 * 0 в обоих режимах — «по содержимому») (phase19.16, партия SB120, этап 1;
 * uiux-sb120-stage1 «Дополнение» §2.1; данные db-design-sb120-stage1 §1,
 * решение (а)). Ползунок — живой предпросмотр на канве, «↺» — умолчание
 * режима (600 px / 100 %).
 */

import type { SectionHeight } from "@chaiberi/shared";

import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { useSectionForm } from "../section-form-context";
import { ownValue } from "../../../../lib/site-fields/number-input";
import { HEIGHT_DEFAULTS, heightRange } from "../../../../lib/site-builder/look-size";

type ValueHeight = Extract<SectionHeight, { kind: "number" | "screen" }>;

interface Props {
  height: ValueHeight;
  /** Путь предпросмотра: `look.height.value` / `look.heightPhone.value`. */
  path: string;
  onChange: (height: SectionHeight) => void;
  /**
   * Своего значения ещё нет (телефон «как на ПК»): перед первым кадром
   * предпросмотра записать режим в форму — канва получает вид высоты (kind)
   * раньше числа, а не угадывает его по пути.
   */
  ensureOwn?: () => void;
}

export function HeightValueField({ height, path, onChange, ensureOwn }: Props) {
  const { preview } = useSectionForm();
  const screen = height.kind === "screen";
  const fallback = HEIGHT_DEFAULTS[height.kind];
  const range = heightRange(height.kind);
  return (
    <NumberSliderField
      label={screen ? "Доля экрана" : "Высота не меньше"}
      value={ownValue(height.value, fallback)}
      inherit={{ value: fallback, label: screen ? "весь экран" : `${fallback} px` }}
      min={range.min}
      max={range.max}
      step={1}
      largeStep={screen ? 10 : 40}
      unit={screen ? "%" : "px"}
      display={{ factor: 1, decimals: 2 }}
      presets={screen ? [{ label: "Половина 50", value: 50 }, { label: "Первый экран 100", value: 100 }] : undefined}
      hint={screen ? "% экрана без шапки. 0 — по содержимому." : "Если текст выше — блок вырастет. 0 — по содержимому."}
      onChange={(v) => onChange({ kind: height.kind, value: v ?? fallback })}
      onPreview={(v) => {
        ensureOwn?.();
        preview(path, v ?? fallback);
      }}
    />
  );
}
